import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Alert,
  IconButton,
  Collapse,
  Tooltip,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import {
  Business as BusinessIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Storage as DatabaseIcon,
} from '@mui/icons-material'
import { useState, useCallback, useEffect } from 'react'
import { usePOTracking } from '@/hooks/usePOTracking'
import type { InvoiceExtraction } from '@/types/invoiceAnalyser'

interface ExtractionResultProps {
  result: InvoiceExtraction
  onViewEvidence?: () => void
  onAddSupplierToWhitelist?: (supplierName: string) => void
  onAddRecipientToWhitelist?: (recipientName: string) => void
  onAddToDatabase?: (result: InvoiceExtraction) => void
}

const CONFIDENCE_COLORS = {
  high: 'success',
  medium: 'warning',
  low: 'error',
} as const
const MAX_PO_DESCRIPTION_LENGTH = 10

const truncateDescription = (description: string | null): string => {
  if (!description) return ''
  const trimmed = description.trim()
  if (!trimmed) return ''
  return trimmed.length > MAX_PO_DESCRIPTION_LENGTH
    ? `${trimmed.slice(0, MAX_PO_DESCRIPTION_LENGTH)}...`
    : trimmed
}

const formatPOLabel = (po: { poNumber: string; description: string | null }): string => {
  const truncated = truncateDescription(po.description)
  return truncated ? `${po.poNumber} - ${truncated}` : po.poNumber
}

const parseYearFromDateString = (dateStr: string | null | undefined): number | null => {
  if (!dateStr) return null
  const trimmed = dateStr.trim()
  if (!trimmed) return null

  const isoMatch = /^(\d{4})-\d{2}-\d{2}/.exec(trimmed)
  if (isoMatch) return Number(isoMatch[1])

  const deMatch = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(trimmed)
  if (deMatch) return Number(deMatch[3])

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.getFullYear()
}

const getInvoiceYear = (
  dateStr: string | null | undefined,
  fallbackDateStr: string | null | undefined,
): number => {
  const primaryYear = parseYearFromDateString(dateStr)
  if (primaryYear) return primaryYear

  const fallbackYear = parseYearFromDateString(fallbackDateStr)
  if (fallbackYear) return fallbackYear

  return new Date().getFullYear()
}

interface EditableFieldRowProps {
  icon: React.ReactElement
  label: string
  value: string | number | null
  fieldType?: 'text' | 'number' | 'date'
  onValueChange?: (value: string) => void
  isEditing?: boolean
  onToggleEdit?: () => void
  action?: React.ReactElement
  secondary?: string
}

function EditableFieldRow({
  icon,
  label,
  value,
  fieldType = 'text',
  onValueChange,
  isEditing = false,
  onToggleEdit,
  action,
  secondary,
}: EditableFieldRowProps) {
  return (
    <Box sx={{ py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icon}
        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {onToggleEdit && (
          <IconButton size="small" onClick={onToggleEdit} sx={{ ml: 'auto' }}>
            {isEditing ? <SaveIcon fontSize="small" color="primary" /> : <EditIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
        {isEditing ? (
          <TextField
            value={value ?? ''}
            onChange={(e) => onValueChange?.(e.target.value)}
            type={fieldType === 'number' ? 'number' : 'text'}
            size="small"
            fullWidth
            sx={{ flexGrow: 1 }}
          />
        ) : (
          <Typography variant="body1" sx={{ flexGrow: 1 }}>
            {value ?? <Typography component="span" color="textSecondary">Not detected</Typography>}
          </Typography>
        )}
        {action}
      </Box>
      {secondary && (
        <Typography variant="caption" color="textSecondary" sx={{ ml: 3 }}>
          {secondary}
        </Typography>
      )}
    </Box>
  )
}

export function ExtractionResult({
  result,
  onViewEvidence,
  onAddSupplierToWhitelist,
  onAddRecipientToWhitelist,
  onAddToDatabase,
}: ExtractionResultProps) {
  const [showWarnings, setShowWarnings] = useState(true)
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editedResult, setEditedResult] = useState<InvoiceExtraction>(result)
  const [selectedPOId, setSelectedPOId] = useState<string | null>(result.po_id || null)
  const [poSaveMessage, setPoSaveMessage] = useState<string | null>(null)

  // PO Tracking
  const { usePOs } = usePOTracking()
  const invoiceYear = getInvoiceYear(editedResult.invoice_date, result.invoice_date)
  const { data: pos = [], isLoading: posLoading } = usePOs(invoiceYear)

  // Update edited result when original result changes
  useEffect(() => {
    setEditedResult(result)
    setSelectedPOId(result.po_id || null)
    setPoSaveMessage(null)
  }, [result])

  useEffect(() => {
    if (!poSaveMessage) return
    const timer = setTimeout(() => setPoSaveMessage(null), 2000)
    return () => clearTimeout(timer)
  }, [poSaveMessage])

  const handleFieldChange = useCallback((field: keyof InvoiceExtraction, value: string) => {
    setEditedResult((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleToggleEdit = useCallback((field: string) => {
    if (editingField === field) {
      setEditingField(null)
    } else {
      setEditingField(field)
    }
  }, [editingField])

  const handleAddToDatabase = useCallback(() => {
    onAddToDatabase?.({ ...editedResult, po_id: selectedPOId })
  }, [editedResult, selectedPOId, onAddToDatabase])

  const handlePOSelection = useCallback((event: SelectChangeEvent<string>) => {
    const poId = event.target.value || null
    setSelectedPOId(poId)

    if (poId && onAddToDatabase) {
      onAddToDatabase({ ...editedResult, po_id: poId })
      setPoSaveMessage('Saved')
    } else {
      setPoSaveMessage(null)
    }
  }, [editedResult, onAddToDatabase])

  const formatCurrency = (amount: number | null, currency: string | null) => {
    if (amount === null) return null
    const formatted = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
    return `${formatted} ${currency || ''}`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null
    try {
      const date = new Date(dateStr)
      return new Intl.DateTimeFormat('de-DE').format(date)
    } catch {
      return dateStr
    }
  }

  const confidenceColor = CONFIDENCE_COLORS[result.confidence]
  const hasPOs = pos.length > 0
  const poSelectDisabled = posLoading || !hasPOs
  const poHelperText = poSaveMessage
    ? poSaveMessage
    : posLoading
      ? 'Loading POs...'
      : !hasPOs
        ? 'No POs for invoice year'
        : null

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Extraction Result</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={result.confidence.toUpperCase()}
            color={confidenceColor as any}
            size="small"
          />
          {onViewEvidence && (
            <Chip
              icon={<VisibilityIcon />}
              label="Evidence"
              variant="outlined"
              size="small"
              onClick={onViewEvidence}
              sx={{ cursor: 'pointer' }}
            />
          )}
          <FormControl size="small" sx={{ minWidth: 220 }} disabled={poSelectDisabled}>
            <InputLabel>Mapping to PO</InputLabel>
            <Select
              value={selectedPOId || ''}
              onChange={handlePOSelection}
              label="Mapping to PO"
            >
              <MenuItem value="">
                <em>No PO</em>
              </MenuItem>
              {pos.map((po) => (
                <MenuItem key={po.id} value={po.id}>
                  {formatPOLabel(po)}
                </MenuItem>
              ))}
            </Select>
            {poHelperText && (
              <FormHelperText sx={poSaveMessage ? { color: 'success.main' } : undefined}>
                {poHelperText}
              </FormHelperText>
            )}
          </FormControl>
          {onAddToDatabase && (
            <Button
              variant="contained"
              startIcon={<DatabaseIcon />}
              onClick={handleAddToDatabase}
              size="small"
            >
              Add to Database
            </Button>
          )}
        </Stack>
      </Stack>

      {/* FX Conversion Notice */}
      {editedResult.currency && editedResult.currency !== 'EUR' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Will be saved in EUR using historical exchange rate from {formatDate(editedResult.invoice_date) || 'the invoice date'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Invoice Details
          </Typography>

          <EditableFieldRow
            icon={<DescriptionIcon fontSize="small" />}
            label="Invoice Number"
            value={editedResult.invoice_number}
            isEditing={editingField === 'invoice_number'}
            onToggleEdit={() => handleToggleEdit('invoice_number')}
            onValueChange={(value) => handleFieldChange('invoice_number', value)}
          />

          <EditableFieldRow
            icon={<CalendarIcon fontSize="small" />}
            label="Invoice Date"
            value={formatDate(editedResult.invoice_date)}
            fieldType="date"
            isEditing={editingField === 'invoice_date'}
            onToggleEdit={() => handleToggleEdit('invoice_date')}
            onValueChange={(value) => handleFieldChange('invoice_date', value)}
          />

          <EditableFieldRow
            icon={<BusinessIcon fontSize="small" />}
            label="Supplier"
            value={editedResult.supplier}
            isEditing={editingField === 'supplier'}
            onToggleEdit={() => handleToggleEdit('supplier')}
            onValueChange={(value) => handleFieldChange('supplier', value)}
            action={
              editedResult.supplier && onAddSupplierToWhitelist ? (
                <Tooltip title="Add to supplier whitelist">
                  <IconButton
                    size="small"
                    onClick={() => onAddSupplierToWhitelist(editedResult.supplier!)}
                    color="primary"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }
          />

          <EditableFieldRow
            icon={<PersonIcon fontSize="small" />}
            label="Recipient"
            value={editedResult.recipient}
            isEditing={editingField === 'recipient'}
            onToggleEdit={() => handleToggleEdit('recipient')}
            onValueChange={(value) => handleFieldChange('recipient', value)}
            action={
              editedResult.recipient && onAddRecipientToWhitelist ? (
                <Tooltip title="Add to recipient whitelist">
                  <IconButton
                    size="small"
                    onClick={() => onAddRecipientToWhitelist(editedResult.recipient!)}
                    color="primary"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : undefined
            }
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Financial Details
          </Typography>

          <EditableFieldRow
            icon={<MoneyIcon fontSize="small" />}
            label="Currency"
            value={editedResult.currency}
            isEditing={editingField === 'currency'}
            onToggleEdit={() => handleToggleEdit('currency')}
            onValueChange={(value) => handleFieldChange('currency', value)}
          />

          <EditableFieldRow
            icon={<MoneyIcon fontSize="small" />}
            label="Net Amount"
            value={formatCurrency(editedResult.total_net, editedResult.currency)}
            fieldType="number"
            isEditing={editingField === 'total_net'}
            onToggleEdit={() => handleToggleEdit('total_net')}
            onValueChange={(value) => handleFieldChange('total_net', value)}
          />

          <EditableFieldRow
            icon={<MoneyIcon fontSize="small" />}
            label="VAT Amount"
            value={formatCurrency(editedResult.vat_amount, editedResult.currency)}
            fieldType="number"
            isEditing={editingField === 'vat_amount'}
            onToggleEdit={() => handleToggleEdit('vat_amount')}
            onValueChange={(value) => handleFieldChange('vat_amount', value)}
          />

          <EditableFieldRow
            icon={<MoneyIcon fontSize="small" />}
            label="Tax-Free Amount"
            value={formatCurrency(editedResult.tax_free_amount, editedResult.currency)}
            fieldType="number"
            isEditing={editingField === 'tax_free_amount'}
            onToggleEdit={() => handleToggleEdit('tax_free_amount')}
            onValueChange={(value) => handleFieldChange('tax_free_amount', value)}
          />

          <EditableFieldRow
            icon={<MoneyIcon fontSize="small" />}
            label="Taxable Net Amount"
            value={formatCurrency(editedResult.taxable_net_amount, editedResult.currency)}
            fieldType="number"
            isEditing={editingField === 'taxable_net_amount'}
            onToggleEdit={() => handleToggleEdit('taxable_net_amount')}
            onValueChange={(value) => handleFieldChange('taxable_net_amount', value)}
          />

          <EditableFieldRow
            icon={<MoneyIcon fontSize="small" />}
            label="Gross Amount"
            value={formatCurrency(editedResult.total_gross, editedResult.currency)}
            fieldType="number"
            isEditing={editingField === 'total_gross'}
            onToggleEdit={() => handleToggleEdit('total_gross')}
            onValueChange={(value) => handleFieldChange('total_gross', value)}
          />

          <EditableFieldRow
            icon={<DescriptionIcon fontSize="small" />}
            label="VAT ID (USt-ID)"
            value={editedResult.ust_id}
            isEditing={editingField === 'ust_id'}
            onToggleEdit={() => handleToggleEdit('ust_id')}
            onValueChange={(value) => handleFieldChange('ust_id', value)}
          />

          {editedResult.project_name !== null && (
            <EditableFieldRow
              icon={<DescriptionIcon fontSize="small" />}
              label="Project"
              value={editedResult.project_name}
              isEditing={editingField === 'project_name'}
              onToggleEdit={() => handleToggleEdit('project_name')}
              onValueChange={(value) => handleFieldChange('project_name', value)}
            />
          )}
        </Paper>
      </Box>

      {editedResult.warnings && editedResult.warnings.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => setShowWarnings(!showWarnings)}
          >
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              Warnings ({editedResult.warnings.length})
            </Typography>
            {showWarnings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Stack>
          <Collapse in={showWarnings}>
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {editedResult.warnings.map((warning, idx) => (
                  <Typography component="li" key={idx} variant="body2">
                    {warning}
                  </Typography>
                ))}
              </Box>
            </Alert>
          </Collapse>
        </Box>
      )}
    </Paper>
  )
}
